import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

// Define SitePage interface locally to avoid importing from useSitePages.ts
// which has a static Supabase import that would leak into the entry bundle
export interface SitePage {
  id: string;
  page_key: string;
  name: string;
  path: string;
  parent_key: string | null;
  is_visible: boolean;
  sort_order: number;
  custom_title: string | null;
  custom_content: string | null;
  meta_description: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Hook that defers the site_pages query until after window.load AND browser idle.
 * This removes the Supabase call from the critical request chain on initial page load.
 * Navigation.tsx uses static fallback data until this query completes.
 */
export function useDeferredVisibleSitePages() {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timerId: ReturnType<typeof setTimeout> | undefined;

    const scheduleReady = () => {
      // Hard gate: 2× rAF + 7s timeout to ensure we're well past LCP measurement
      requestAnimationFrame(() => {
        if (cancelled) return;
        requestAnimationFrame(() => {
          if (cancelled) return;
          timerId = setTimeout(() => {
            if (!cancelled) {
              setIsReady(true);
            }
          }, 7000);
        });
      });
    };

    // Wait for window.load FIRST, then schedule hard gate
    if (document.readyState === 'complete') {
      scheduleReady();
    } else {
      const handleLoad = () => {
        scheduleReady();
      };
      window.addEventListener('load', handleLoad);
      return () => {
        cancelled = true;
        window.removeEventListener('load', handleLoad);
        if (timerId) clearTimeout(timerId);
      };
    }

    return () => {
      cancelled = true;
      if (timerId) clearTimeout(timerId);
    };
  }, []);

  return useQuery({
    queryKey: ['site-pages-visible'],
    queryFn: async () => {
      // Dynamic import - only loads Supabase SDK when query actually runs
      const { supabase } = await import('@/integrations/supabase/client');
      const { data, error } = await supabase
        .from('site_pages')
        .select('*')
        .eq('is_visible', true)
        .order('sort_order');
      
      if (error) throw error;
      return data as SitePage[];
    },
    enabled: isReady, // Only fetch after window.load + 2×rAF + 7s
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 0, // No retries - static fallback handles missing data
  });
}
